import { skipToken } from '@apollo/client/react'
import { type FC, type ReactElement, useCallback } from 'react'
import { match } from 'ts-pattern'
import * as v from 'valibot'
import ArtworkFrame from 'src/components/Artwork/ArtworkFrame'
import AvatarFrame from 'src/components/UserAvatar/AvatarFrame'
import UserBadgeToken from 'src/components/UserBadgeToken'
import { DecorationLiveBannerDataSchema } from 'src/features/UserDecoration/schema/decoration'
import {
  type DecorationBaseFragment,
  DecorationType,
} from 'src/services/apollo/generated/graphql'
import { useDecoration } from 'src/services/apollo/user-decoration'
import { cn } from 'src/utils/cx'
import type { Decoration } from '~/services/apollo/generated/graphql'

type DecorationComponentProps = {
  /** Only applicable to ArtworkFrame */
  fillClassName?: string
} & StyleProps

export type DecorationRendererProps = StyleProps & {
  code?: string
  /**
   * The renderer component. Will render a bare Decoration component if not provided.
   */
  children?: (
    data: DecorationBaseFragment,
    Decoration: FC<DecorationComponentProps>,
  ) => ReactElement
}

export default function DecorationRenderer({
  code,
  children,
  className,
  style,
}: DecorationRendererProps) {
  const { data } = useDecoration(code ? { code } : skipToken)
  const Decoration = useCallback(
    (props: DecorationComponentProps) =>
      match(data)
        .with({ type: DecorationType.Badge }, () => (
          <UserBadgeToken
            decoration={data}
            className={cn(className, props.className)}
            style={{ ...style, ...props.style }}
          />
        ))
        .with({ type: DecorationType.AvatarFrame }, () => (
          <div
            className={cn('relative', className, props.className)}
            style={{ ...style, ...props.style }}
          >
            <AvatarFrame decoration={data} className="scale-125" />
          </div>
        ))
        .with({ type: DecorationType.ArtworkFrame }, () => (
          <div
            className={cn('relative', className, props.className)}
            style={{ ...style, ...props.style }}
          >
            <ArtworkFrame
              decoration={data}
              fillClassName={cn('bg-white', props.fillClassName)}
            />
          </div>
        ))
        .with({ type: DecorationType.LiveBanner }, () => {
          return (
            <LiveBannerPreview
              decoration={data}
              className={cn('object-cover', className, props.className)}
              style={{ ...style, ...props.style }}
            />
          )
        })
        .otherwise(() => null),
    [className, data, style],
  )

  return data ? (
    children ? (
      children(data, Decoration)
    ) : (
      <Decoration className={className} style={style} />
    )
  ) : null
}

function LiveBannerPreview(props: { decoration?: Decoration } & StyleProps) {
  const { decoration, className, style } = props
  const data = v.safeParse(DecorationLiveBannerDataSchema, decoration?.data)
  if (!data.success) return null
  return (
    <img
      src={data.output.previewMediaAsset.original}
      className={cn('object-cover', className)}
      style={style}
      alt=""
    />
  )
}
